// PeopleManager.jsx
// Standalone people linker for Projects or Proposals
// Props:
//   - entityType: 'compass_projects' | 'proposals'  (determines endpoint prefix)
//   - entityId: string (the project/proposal id)
//
// Behavior:
// - Shows current people linked to the entity.
// - "Manage" opens a Dialog with two tables:
//     * Linked People (remove button per row)
//     * Available People (search + add button per row)
// - Endpoints assumed:
//     /api/projects/:id/people   (GET list, POST { person_id }, DELETE /:personId)
//     /api/proposals/:id/people  (GET list, POST { person_id }, DELETE /:personId)
//     /api/people?q=...          (search people)

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Stack,
  Text,
  Label,
  PrimaryButton,
  DefaultButton,
  IconButton,
  SearchBox,
  Spinner,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  Dialog,
  DialogType,
  DialogFooter,
  Persona,
  Separator,
} from '@fluentui/react';
import { Link } from 'react-router-dom/cjs/react-router-dom';

const api = axios.create({ baseURL: '/api' });

export default function PeopleManager({ entityType, entityId, className, asPersona=true }) {
  const [linked, setLinked] = useState([]);   // [{ id, name, title? }]
  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState('');

  const [isOpen, setIsOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const basePrefix = useMemo(() => (entityType === 'compass_projects' ? '/compass_projects' : '/proposals'), [entityType]);
  const listUrl   = useMemo(() => `${basePrefix}/${entityId}/members`, [basePrefix, entityId]);
  const addUrl    = listUrl; // POST same path

  const loadLinked = useCallback(async () => {
    if (!entityId) return;
    setLoading(true); setError('');
    try {
      const r = await api.get(listUrl);
      setLinked(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || e.message || 'Failed to load people');
    } finally { setLoading(false); }
  }, [listUrl, entityId]);

  useEffect(() => { loadLinked(); }, [loadLinked]);

  // Debounced search for available people (exclude already linked)
  useEffect(() => {
    if (!isOpen) return; // only search when dialog open
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get('/people', { params: q ? { q } : {}, signal: ctrl.signal });
        const all = Array.isArray(r.data.data) ? r.data.data : [];
        const linkedIds = new Set(linked.map(p => p.id));
        setResults(all.filter(p => !linkedIds.has(p.id)));
      } catch (e) {
        if (e.code !== 'ERR_CANCELED') console.error(e);
      } finally { setSearching(false); }
    }, 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q, isOpen, linked]);

  const addPerson = async (personId) => {
    try {
      setMutating(true);
      await api.post(addUrl, { person_id: personId });
      await loadLinked();
      setQ('');
    } catch (e) { console.error(e); alert('Failed to add'); }
    finally { setMutating(false); }
  };

  const removePerson = async (personId) => {
    try {
      setMutating(true);
      await api.delete(`${listUrl}/${personId}`);
      setLinked(prev => prev.filter(p => p.id !== personId));
    } catch (e) { console.error(e); alert('Failed to remove'); }
    finally { setMutating(false); }
  };

  const linkedColumns = [
    { key: 'name', name: 'Name', fieldName: 'name', minWidth: 180, isResizable: true },
    { key: 'title', name: 'Title', fieldName: 'title', minWidth: 120 },
    { key: 'actions', name: 'Actions', minWidth: 90, onRender: (it) => (
      <IconButton iconProps={{ iconName: 'Cancel' }} title="Remove" ariaLabel="Remove" onClick={() => removePerson(it.id)} />
    )},
  ];

  const resultsColumns = [
    { key: 'name', name: 'Name', fieldName: 'name', minWidth: 180, isResizable: true },
    { key: 'title', name: 'Title', fieldName: 'title', minWidth: 120 },
    { key: 'add', name: 'Add', minWidth: 80, onRender: (it) => (
      <IconButton iconProps={{ iconName: 'Add' }} title="Add" ariaLabel="Add" onClick={() => addPerson(it.id)} />
    )},
  ];

  return (
    <>
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
        <Stack>
          <Text variant='xLarge'>People</Text>
        </Stack>
        <DefaultButton text="Manage" onClick={() => setIsOpen(true)} />
      </Stack>
      <Separator />

      {/* Compact list preview */}
      {loading ? (
        <Spinner />
      ) : linked.length === 0 ? (
        <Text variant="small">No people linked.</Text>
      ) : asPersona ? (
        <Stack horizontal wrap tokens={{childrenGap: 16}}>
          {linked.map(item => <Link key={item.id} to={`/people/${item.id}`}><Persona text={item.name} secondaryText={item.title} imageUrl={item.image_url} /></Link> )}
        </Stack>
      ) : (
        <DetailsList
          items={linked}
          columns={[{ key: 'name', name: 'Name', fieldName: 'name', minWidth: 180 }, { key: 'title', name: 'Title', fieldName: 'title', minWidth: 120 }]}
          selectionMode={SelectionMode.none}
          compact
        />
      )}

      {/* Dialog for full management */}
      <Dialog
        hidden={!isOpen}
        onDismiss={() => setIsOpen(false)}
        minWidth={720}
        dialogContentProps={{ type: DialogType.largeHeader, title: 'Manage People' }}
      >
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Label>Linked</Label>
            {(mutating) && <Spinner label="Saving" />}
          </Stack>
          <DetailsList
            items={linked}
            columns={linkedColumns}
            layoutMode={DetailsListLayoutMode.justified}
            selectionMode={SelectionMode.none}
            compact
          />

          <Label>Available</Label>
          <SearchBox placeholder="Search people…" value={q} onChange={(_, v) => setQ(v || '')} styles={{ root: { maxWidth: 320 } }} />
          {searching && <Spinner label="Searching" />}
          <DetailsList
            items={results}
            columns={resultsColumns}
            layoutMode={DetailsListLayoutMode.justified}
            selectionMode={SelectionMode.none}
            compact
          />
        </Stack>

        <DialogFooter>
          <DefaultButton onClick={() => setIsOpen(false)} text="Close" />
        </DialogFooter>
      </Dialog>
    </>
  );
}
